import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { useGetDisplayName } from '@frontend/get-display-name';
import { useTranslation } from '@frontend/i18n';
import { Photos } from '@frontend/photos';
import { theme } from '@frontend/theme';
import { Button, styles, Text } from '@frontend/design-system';

type ChangeContextContactButtonProps = {
  person: Person;
  isPrimaryContact: boolean;
  onChange: () => void;
};

export const ChangeContextContactButton = ({ person, isPrimaryContact, onChange }: ChangeContextContactButtonProps) => {
  const { t } = useTranslation('inbox-templates');
  const { fullName, firstName: displayFirstName } = useGetDisplayName({
    person: {
      firstName: person.firstName || '',
      lastName: person.lastName || '',
      preferredName: person.preferredName,
    },
  });

  return (
    <div
      css={{
        backgroundColor: theme.colors.white,
        borderRadius: theme.borderRadius.medium,
        display: 'flex',
        gap: theme.spacing(0.5),
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: theme.spacing(0.5),
      }}
      data-testid='change-context-contact-button-wrapper'
    >
      <div
        css={{
          display: 'flex',
          gap: theme.spacing(1),
          padding: theme.spacing(0.5),
          alignItems: 'center',
        }}
      >
        <Photos.ContactProfilePhoto
          personId={person.personId}
          name={fullName}
          firstName={displayFirstName}
          lastName={person.lastName}
          size='small'
          disableClick
          disableHoverLabel
        />
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Text
            css={[
              styles.truncate,
              {
                maxWidth: 125,
                textWrap: isPrimaryContact ? 'nowrap' : 'pretty',
                lineClamp: 2,
                lineHeight: 1.15,
              },
            ]}
            size='medium'
          >
            <Text as='span' weight='bold' css={{ lineHeight: 'inherit' }}>
              {displayFirstName}
            </Text>
            <Text as='span' css={{ lineHeight: 'inherit' }}>{` ${person.lastName ?? ''}`}</Text>
          </Text>
          {isPrimaryContact && (
            <Text size='small' color='light' css={{ lineHeight: 'inherit' }}>
              {t('Phone Owner')}
            </Text>
          )}
        </div>
      </div>
      <Button
        hoverLabel={t('Focus Another Contact')}
        onClick={onChange}
        variant='tertiary'
        size='small'
        trackingId='context-contact-focus-menu-open-button'
      >
        {t('Change')}
      </Button>
    </div>
  );
};
